import { useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';

import type {
  InsuranceStatusCtaValue,
  StatusTypeValue,
  ZuubPatientListItemV3,
} from '@/@types/zuub/contracts';
import { buildClaimTabData } from '@/modules/annotator/visitSidebar/claimTab/buildClaimTabData';
import { useClaimSummary } from '@/modules/claims/freeClaims/hooks/useClaimSummary';
import { useGetZuubInsuranceList } from '@/react-query/useGetZuubInsuranceList';
import { usePreferences } from '@/react-query/usePreferences';

export interface ClaimTabAppointmentData {
  readonly dateOfService?: string;
  readonly patientName?: string;
  readonly payerName?: string;
}

export interface ClaimTabInsuranceEligibility {
  readonly payerName: string;
  readonly statusLabel?: string;
  readonly statusVariant: 'default' | 'error' | 'success' | 'warning';
}

function getInsuranceStatusLabel(
  cta: InsuranceStatusCtaValue | string | null | undefined,
): string | undefined {
  switch (cta) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    case 'CHECK_MANUALLY':
      return 'Check manually';
    case 'CHECK_VIDEA_LOGIN':
      return 'Check Videa log-in';
    case 'CONFIRM_PATIENT_DETAILS':
      return 'Confirm patient details';
    case 'NOT_ELIGIBLE':
      return 'Not Eligible';
    case 'CONFIRM_NPI_NUMBER':
      return 'Confirm NPI number';
    case 'NO_PAYER_LISTED':
      return 'No Payer Listed';
    default:
      return cta ?? undefined;
  }
}

function getStatusVariant(
  statusType: StatusTypeValue | null | undefined,
): ClaimTabInsuranceEligibility['statusVariant'] {
  switch (statusType) {
    case 'success':
      return 'success';
    case 'warn':
      return 'warning';
    case 'error':
      return 'error';
    case 'action':
      return 'default';
    default:
      return 'default';
  }
}

function getPrimaryPlan(zuubInsurance: ZuubPatientListItemV3 | undefined) {
  const plans = zuubInsurance?.plans ?? [];
  if (plans.length === 0) {
    return null;
  }

  const sortedPlans = [...plans].sort((a, b) => {
    const isActiveA = a.insuranceStatusV2 === 'ACTIVE';
    const isActiveB = b.insuranceStatusV2 === 'ACTIVE';
    if (isActiveA !== isActiveB) {
      return isActiveA ? -1 : 1;
    }
    return a.order - b.order;
  });

  return sortedPlans[0] ?? null;
}

export function useClaimTabData(input: {
  estimatedVisitId?: number | null;
  enabled: boolean;
  imagingPatientVideaId?: string | null;
  unifiedAppointmentId?: number | null;
}) {
  const { t } = useLingui();
  const { data: prefs } = usePreferences();
  const isInsuranceEligibilityEnabled =
    input.enabled && Boolean(prefs?.isInsuranceEligibilityEnabled);
  const { data: zuubInsuranceList } = useGetZuubInsuranceList({
    unifiedIds: input.unifiedAppointmentId
      ? [String(input.unifiedAppointmentId)]
      : [],
    enabled:
      isInsuranceEligibilityEnabled && Boolean(input.unifiedAppointmentId),
  });
  const { claimSummary, isLoadingClaimSummary } = useClaimSummary({
    estimatedVisitId:
      input.enabled && input.estimatedVisitId
        ? input.estimatedVisitId
        : undefined,
    imagingPatientVideaId:
      input.enabled && input.imagingPatientVideaId
        ? input.imagingPatientVideaId
        : undefined,
    unifiedAppointmentId: input.enabled
      ? (input.unifiedAppointmentId ?? 0)
      : 0,
  });

  const data = useMemo(() => {
    if (!claimSummary) {
      return buildClaimTabData({
        treatments: [],
        messages: [],
      });
    }

    return buildClaimTabData({
      treatments: claimSummary.treatments ?? [],
      messages: claimSummary.messages ?? [],
    });
  }, [claimSummary]);

  const insuranceEligibility = useMemo<
    ClaimTabInsuranceEligibility | undefined
  >(() => {
      if (!isInsuranceEligibilityEnabled) {
        return undefined;
      }

      const primaryPlan = getPrimaryPlan(
        zuubInsuranceList?.patients?.find(
          (patient) =>
            patient.unifiedAppointmentId === String(input.unifiedAppointmentId),
        ),
      );
      const payerName = primaryPlan?.payerName;
      if (!payerName) {
        return undefined;
      }

      const rawStatusLabel = getInsuranceStatusLabel(
        primaryPlan?.insuranceStatusV2,
      );
      const translatedStatusLabel = rawStatusLabel
        ? ({
            Active: t`Active`,
            Inactive: t`Inactive`,
            'Check manually': t`Check manually`,
            'Check Videa log-in': t`Check Videa log-in`,
            'Confirm patient details': t`Confirm patient details`,
            'Not Eligible': t`Not Eligible`,
            'Confirm NPI number': t`Confirm NPI number`,
            'No Payer Listed': t`No Payer Listed`,
          }[rawStatusLabel] ?? rawStatusLabel)
        : undefined;

      return {
        payerName,
        statusLabel: translatedStatusLabel,
        statusVariant: getStatusVariant(primaryPlan?.statusType),
      };
  }, [
    input.unifiedAppointmentId,
    isInsuranceEligibilityEnabled,
    t,
    zuubInsuranceList,
  ]);

  return {
    ...data,
    appointmentData: claimSummary as ClaimTabAppointmentData | undefined,
    insuranceEligibility,
    isLoading: isLoadingClaimSummary,
  };
}
